import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import { PageHeader } from "@/components/platform/platformUi";
import { formatBlogDate } from "@/lib/seo";

const STATUS_STYLE = {
    published: "bg-[#DCEEDC] text-[#1F7A55]",
    draft: "bg-[#FDF3DC] text-[#9A7B10]",
};

export default function PlatformBlog() {
    const [posts, setPosts] = useState([]);
    const [filter, setFilter] = useState("all");
    const [busy, setBusy] = useState(false);

    const load = () => {
        const params = filter === "all" ? {} : { status: filter };
        api.get("/platform/blog", { params })
            .then((r) => setPosts(r.data || []))
            .catch(() => toast.error("Could not load blog posts"));
    };

    useEffect(() => { load(); }, [filter]);

    const remove = async (post) => {
        if (!window.confirm(`Delete "${post.title}"?`)) return;
        setBusy(true);
        try {
            await api.delete(`/platform/blog/${post.id}`);
            toast.success("Post deleted");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="platform-blog-page">
            <PageHeader
                eyebrow="Content"
                title="Blog"
                subtitle="Publish articles for your marketing site — SEO-ready with rich formatting"
                action={
                    <Link to="/platform/blog/new">
                        <Button type="button" className="bg-[#4A0E17] text-white hover:bg-[#3a0b12]">
                            <Plus className="mr-1.5 size-4" />
                            New post
                        </Button>
                    </Link>
                }
            />

            <div className="flex flex-wrap gap-2">
                {[
                    ["all", "All"],
                    ["published", "Published"],
                    ["draft", "Drafts"],
                ].map(([key, label]) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => setFilter(key)}
                        className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                            filter === key
                                ? "bg-[#4A0E17] text-white"
                                : "border border-[#E8E4DE] bg-white text-[#8A8178] hover:text-[#4A0E17]"
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            <div className="overflow-x-auto rounded-xl border border-[#E8E4DE] bg-white shadow-sm">
                <table className="min-w-full border-collapse text-sm">
                    <thead>
                        <tr className="border-b border-[#E8E4DE] text-left text-[11px] font-bold uppercase tracking-[0.12em] text-[#8A8178]">
                            <th className="px-4 py-3">Title</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Updated</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {posts.map((post) => (
                            <tr key={post.id} className="border-b border-[#E8E4DE]/60 last:border-b-0">
                                <td className="px-4 py-3">
                                    <p className="font-semibold text-[#4A0E17]">{post.title}</p>
                                    <p className="text-xs text-[#8A8178]">/blog/{post.slug}</p>
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${STATUS_STYLE[post.status] || STATUS_STYLE.draft}`}>
                                        {post.status}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-[#8A8178]">
                                    {formatBlogDate(post.updated_at || post.created_at)}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex justify-end gap-2">
                                        {post.status === "published" && (
                                            <a
                                                href={`/blog/${post.slug}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex size-8 items-center justify-center rounded-md border border-[#E8E4DE] text-[#8A8178] hover:text-[#4A0E17]"
                                                title="View live"
                                            >
                                                <ExternalLink className="size-4" />
                                            </a>
                                        )}
                                        <Link
                                            to={`/platform/blog/${post.id}`}
                                            className="inline-flex size-8 items-center justify-center rounded-md border border-[#E8E4DE] text-[#8A8178] hover:text-[#4A0E17]"
                                        >
                                            <Pencil className="size-4" />
                                        </Link>
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={() => remove(post)}
                                            className="inline-flex size-8 items-center justify-center rounded-md border border-[#E8E4DE] text-[#C84B31] hover:bg-[#FEF5F3]"
                                        >
                                            <Trash2 className="size-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {posts.length === 0 && (
                            <tr>
                                <td colSpan={4} className="px-4 py-12 text-center text-[#8A8178]">
                                    No posts yet — create your first article
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
